import { Box, Stack } from "@mui/material";
import { type MutableRefObject, useEffect, useRef } from "react";

/**
 * Bar geometry from the Ask Helena dictation frame, measured against its 32px
 * send button: 22 bars, 2px wide, 2px apart, 16px at the tallest (16px
 * confirmed by design). A silent bar is a 2px dot.
 */
const DEFAULT_BAR_COUNT = 22;
const DEFAULT_MAX_BAR_HEIGHT_PX = 16;
const BAR_WIDTH_PX = 2;
const BAR_GAP_PX = 2;
/** The RMS level that fills a bar — the dictation POC meter's scale (level × 4). */
const FULL_SCALE_LEVEL = 0.25;
/**
 * The dictation design's fill (Figma, 2026-10-08), spread across the WHOLE
 * waveform rather than restarting in each 2px bar. The theme has no gradient
 * tokens; the composer border and Helena status text inline theirs the same way.
 */
const DEFAULT_FILL =
  "linear-gradient(288.1deg, #ED25C1 -83.01%, #F096FF -22.78%, #ECB4E0 -12.51%, #FF96E2 0.85%, #A74FFF 54.27%, #874FFF 70.38%, #4D25ED 97.69%)";
/** More history than any waveform draws, so a wider one never runs short. */
const LEVEL_HISTORY_SIZE = 64;

/**
 * Records one loudness reading for an `AudioLevelWaveform`. `level` is the RMS
 * of a short block of samples (0 = silence, 1 = full scale) — call it each time
 * the audio source produces one (the dictation worklet does every 100 ms).
 */
export const pushAudioLevel = (levels: number[], level: number): void => {
  levels.push(level);
  if (levels.length > LEVEL_HISTORY_SIZE) levels.shift();
};

interface AudioLevelWaveformProps {
  /** Loudness history, newest last — fill it with `pushAudioLevel`. */
  levelsRef: MutableRefObject<number[]>;
  barCount?: number;
  maxBarHeight?: number;
  /**
   * Any CSS background — a colour or a gradient. A gradient spans the whole
   * waveform: each bar paints its own slice of it. Defaults to the dictation
   * design's gradient.
   */
  fill?: string;
}

/**
 * Live loudness waveform: a scrolling history of the mic level, newest bar on
 * the right. Bars are painted on animation frames by writing heights straight
 * to the DOM, so a level arriving every 100 ms never re-renders React.
 */
export const AudioLevelWaveform = ({
  levelsRef,
  barCount = DEFAULT_BAR_COUNT,
  maxBarHeight = DEFAULT_MAX_BAR_HEIGHT_PX,
  fill = DEFAULT_FILL,
}: AudioLevelWaveformProps) => {
  const barsRef = useRef<(HTMLSpanElement | null)[]>([]);
  const barKeys = Array.from(
    { length: barCount },
    (_, index) => `bar-${index}`,
  );

  useEffect(() => {
    const toBarHeight = (level: number): number =>
      Math.max(
        BAR_WIDTH_PX,
        Math.min(1, level / FULL_SCALE_LEVEL) * maxBarHeight,
      );

    let frameId = 0;
    const paint = () => {
      const levels = levelsRef.current.slice(-barCount);
      const offset = barCount - levels.length;
      for (let index = 0; index < barCount; index++) {
        const bar = barsRef.current[index];
        const level = index >= offset ? levels[index - offset] : 0;
        if (!bar) continue;
        const height = toBarHeight(level);
        bar.style.height = `${height}px`;
        // Bars are centred, so a shorter bar starts lower in the box — shift
        // its slice of the gradient up to match.
        bar.style.backgroundPositionY = `${-(maxBarHeight - height) / 2}px`;
      }
      frameId = requestAnimationFrame(paint);
    };
    paint();
    return () => cancelAnimationFrame(frameId);
  }, [levelsRef, barCount, maxBarHeight]);

  const width = barCount * BAR_WIDTH_PX + (barCount - 1) * BAR_GAP_PX;

  return (
    <Stack
      direction="row"
      aria-hidden
      sx={{
        alignItems: "center",
        gap: `${BAR_GAP_PX}px`,
        height: `${maxBarHeight}px`,
      }}
    >
      {barKeys.map((key, index) => (
        <Box
          key={key}
          component="span"
          ref={(element: HTMLSpanElement | null) => {
            barsRef.current[index] = element;
          }}
          sx={{
            flexShrink: 0,
            width: `${BAR_WIDTH_PX}px`,
            height: `${BAR_WIDTH_PX}px`,
            borderRadius: `${BAR_WIDTH_PX / 2}px`,
            background: fill,
            // One gradient the size of the whole waveform, offset to this bar.
            backgroundSize: `${width}px ${maxBarHeight}px`,
            backgroundPositionX: `${-index * (BAR_WIDTH_PX + BAR_GAP_PX)}px`,
          }}
        />
      ))}
    </Stack>
  );
};
